import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { isNativeApp } from "@/lib/native";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Pill, User, FileText, Package, CreditCard, CheckCircle, ArrowRight, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const STEP_ICONS = { profile: User, questionnaire: FileText, medication: Package, payment: CreditCard };
const STEP_COLORS = {
  profile: { bg: "bg-blue-100", text: "text-blue-600", border: "border-blue-200", activeBg: "bg-blue-600" },
  questionnaire: { bg: "bg-purple-100", text: "text-purple-600", border: "border-purple-200", activeBg: "bg-purple-600" },
  medication: { bg: "bg-amber-100", text: "text-amber-600", border: "border-amber-200", activeBg: "bg-amber-600" },
  payment: { bg: "bg-emerald-100", text: "text-emerald-600", border: "border-emerald-200", activeBg: "bg-emerald-600" },
};

export default function PatientOnboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await API.get("/onboarding/status");
      setStatus(data);
      if (data.is_complete) navigate(isNativeApp() ? "/companion" : "/dashboard");
    } catch (e) { console.error("Failed to load onboarding status:", e); } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-8 h-8 border-3 border-purple-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!status) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-100" data-testid="patient-onboarding">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center"><Pill className="w-4 h-4 text-white" /></div>
            <span className="font-bold text-lg text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>GLP<span className="text-purple-600">Flo</span></span>
          </Link>
          <Button variant="ghost" size="sm" className="text-slate-500 rounded-full" onClick={() => navigate("/dashboard")} data-testid="skip-onboarding-btn">
            Skip to Dashboard
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 lg:py-16">
        {/* Welcome */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Welcome to GLP<span className="text-purple-600">Flo</span>, {user?.name?.split(" ")[0]}!
          </h1>
          <p className="text-slate-600 text-lg max-w-lg mx-auto">Let&apos;s get you set up. Complete these steps to place your first medication order.</p>
        </motion.div>

        {/* Progress */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">{status.completed} of {status.total} steps complete</span>
            <span className="text-sm font-bold text-purple-600">{status.progress}%</span>
          </div>
          <Progress value={status.progress} className="h-3" data-testid="onboarding-progress" />
        </div>

        {/* Steps */}
        <div className="space-y-4" data-testid="onboarding-steps">
          {status.steps.map((step, idx) => {
            const StepIcon = STEP_ICONS[step.id] || FileText;
            const colors = STEP_COLORS[step.id] || STEP_COLORS.profile;
            const isCurrent = idx === status.current_step;
            const isLocked = idx > status.current_step && !step.complete;

            return (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <button
                  onClick={() => !isLocked && navigate(step.route)}
                  disabled={isLocked}
                  className={`w-full text-left rounded-2xl border-2 p-6 transition-all duration-300 ${
                    step.complete
                      ? "bg-white border-emerald-200 shadow-[0_4px_20px_rgb(0,0,0,0.03)]"
                      : isCurrent
                        ? `bg-white ${colors.border} shadow-[0_8px_30px_rgb(0,0,0,0.06)] -translate-y-1`
                        : "bg-slate-50 border-slate-200 opacity-60"
                  }`}
                  data-testid={`onboarding-step-${step.id}`}
                >
                  <div className="flex items-center gap-5">
                    {/* Step Number / Check */}
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                      step.complete ? "bg-emerald-100" : isCurrent ? colors.activeBg : colors.bg
                    }`}>
                      {step.complete
                        ? <CheckCircle className="w-7 h-7 text-emerald-600" />
                        : <StepIcon className={`w-7 h-7 ${isCurrent ? "text-white" : colors.text}`} />
                      }
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Step {idx + 1}</span>
                        {step.complete && <span className="text-xs font-bold text-emerald-600 uppercase">Complete</span>}
                        {isCurrent && !step.complete && <span className="text-xs font-bold text-purple-600 uppercase animate-pulse">Current</span>}
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {step.label}
                      </h3>
                      <p className="text-sm text-slate-500 mt-0.5">
                        {step.id === "profile" && "Enter your demographics, address, and medical information"}
                        {step.id === "questionnaire" && "Complete a brief medical intake questionnaire"}
                        {step.id === "medication" && "Browse GLP-1 and peptide medications"}
                        {step.id === "payment" && "Secure checkout and prescriber review"}
                      </p>
                    </div>

                    {/* Arrow */}
                    {!step.complete && !isLocked && (
                      <ChevronRight className={`w-6 h-6 ${isCurrent ? "text-purple-600" : "text-slate-300"}`} />
                    )}
                  </div>
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-10 text-center">
          <Button
            onClick={() => navigate(status.next_route)}
            className="rounded-full bg-purple-600 hover:bg-purple-700 text-white px-10 py-6 text-lg shadow-[0_0_40px_-10px_rgba(147,51,234,0.3)]"
            data-testid="onboarding-continue-btn"
          >
            {status.current_step === 0 ? "Get Started" : status.is_complete ? "Go to Dashboard" : "Continue"} <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
